// src/components/StudentGoalsModal.js
import React, { useEffect, useState } from 'react';
import {
  Modal,
  Button,
  Table,
  Form,
  Spinner,
  Dropdown,
  Badge,
} from 'react-bootstrap';

const StudentGoalsModal = ({ show, onHide, goalsApi }) => {
  const {
    goalsStudent,
    goals = [],
    goalsLoading,
    addGoal,
    startEditGoal,
    cancelEditGoal,
    saveGoal,
    deleteGoal,
    editingGoalId,
    editingGoalText,
    setEditingGoalText,
    editingGoalTerm,
    setEditingGoalTerm,
    editingGoalStatus,
    setEditingGoalStatus,
    editingGoalDueDate,
    setEditingGoalDueDate,
    goalToDelete,
    setGoalToDelete,
  } = goalsApi || {};

  // local add form
  const [newGoalText, setNewGoalText] = useState('');
  const [newGoalTerm, setNewGoalTerm] = useState('');
  const [newGoalStatus, setNewGoalStatus] = useState('in-progress');
  const [newGoalDueDate, setNewGoalDueDate] = useState('');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!goalsStudent) return;
    if (!newGoalText.trim()) return;
    await addGoal({
      goal_text: newGoalText,
      term: newGoalTerm,
      status: newGoalStatus,
      due_date: newGoalDueDate,
    });
    setNewGoalText('');
    setNewGoalTerm('');
    setNewGoalStatus('in-progress');
    setNewGoalDueDate('');
  };

  const handleSave = async () => {
    await saveGoal();
  };

  const handleDelete = async () => {
    await deleteGoal();
  };

  const bodyStyle = {
    maxHeight: isMobile ? '72vh' : '85vh',
    overflowY: 'auto',
  };

  const statusVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'primary';
      case 'incomplete':
        return 'secondary';
      default:
        return 'light';
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered fullscreen="md-down" size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {goalsStudent
              ? `Goals — ${goalsStudent.first_name} ${goalsStudent.last_name}`
              : 'Goals'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={bodyStyle}>
          {/* add form */}
          <Form onSubmit={handleAdd} className="d-flex flex-column gap-2 mb-3">
            <Form.Control
              placeholder="Add a goal"
              value={newGoalText}
              onChange={(e) => setNewGoalText(e.target.value)}
            />
            <div className="d-flex flex-wrap gap-2">
              <Form.Select
                value={newGoalTerm}
                onChange={(e) => setNewGoalTerm(e.target.value)}
                className="flex-grow-1"
              >
                <option value="">Select Term</option>
                <option value="Weekend">Weekend</option>
                <option value="After-school">After-school</option>
                <option value="Summer-dugsi">Summer-dugsi</option>
              </Form.Select>

              <Form.Select
                value={newGoalStatus}
                onChange={(e) => setNewGoalStatus(e.target.value)}
                className="flex-grow-1"
              >
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="incomplete">Incomplete</option>
              </Form.Select>

              <Form.Control
                type="date"
                value={newGoalDueDate}
                onChange={(e) => setNewGoalDueDate(e.target.value)}
                className="flex-grow-1"
              />
            </div>
            <Button type="submit" variant="success">
              Add Goal
            </Button>
          </Form>

          {goalsLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : goals.length === 0 ? (
            <p className="text-muted mb-0">No goals yet.</p>
          ) : (
            <>
              {/* Desktop / tablet */}
              <div className="d-none d-md-block">
                <div className="table-responsive">
                  <Table bordered responsive size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th style={{ minWidth: 180 }}>Goal</th>
                        <th>Term</th>
                        <th>Status</th>
                        <th>Due</th>
                        <th style={{ width: 120 }} className="text-end">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {goals.map((g) => (
                        <tr key={g.id}>
                          <td>
                            {editingGoalId === g.id ? (
                              <Form.Control
                                as="textarea"
                                rows={2}
                                value={editingGoalText}
                                onChange={(e) => setEditingGoalText(e.target.value)}
                              />
                            ) : (
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                {g.goal_text}
                              </div>
                            )}
                          </td>
                          <td>
                            {editingGoalId === g.id ? (
                              <Form.Select
                                value={editingGoalTerm}
                                onChange={(e) => setEditingGoalTerm(e.target.value)}
                              >
                                <option value="">Select Term</option>
                                <option value="Weekend">Weekend</option>
                                <option value="After-school">After-school</option>
                                <option value="Summer-dugsi">Summer-dugsi</option>
                              </Form.Select>
                            ) : (
                              g.term || '—'
                            )}
                          </td>
                          <td>
                            {editingGoalId === g.id ? (
                              <Form.Select
                                value={editingGoalStatus}
                                onChange={(e) => setEditingGoalStatus(e.target.value)}
                              >
                                <option value="in-progress">In Progress</option>
                                <option value="completed">Completed</option>
                                <option value="incomplete">Incomplete</option>
                              </Form.Select>
                            ) : (
                              g.status || '—'
                            )}
                          </td>
                          <td>
                            {editingGoalId === g.id ? (
                              <Form.Control
                                type="date"
                                value={editingGoalDueDate || ''}
                                onChange={(e) => setEditingGoalDueDate(e.target.value)}
                              />
                            ) : g.due_date ? (
                              new Date(g.due_date).toLocaleDateString()
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="text-end">
                            {editingGoalId === g.id ? (
                              <>
                                <Button size="sm" onClick={handleSave}>
                                  Save
                                </Button>{' '}
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={cancelEditGoal}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  size="sm"
                                  variant="outline-secondary"
                                >
                                  Actions
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => startEditGoal(g)}>
                                    Edit
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item
                                    className="text-danger"
                                    onClick={() =>
                                      setGoalToDelete({
                                        id: g.id,
                                        text: g.goal_text,
                                      })
                                    }
                                  >
                                    Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Mobile cards */}
              <div className="d-md-none">
                {goals.map((g) => {
                  const isEditing = editingGoalId === g.id;
                  return (
                    <div key={g.id} className="mb-3 p-3 border rounded bg-light">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="fw-semibold" style={{ maxWidth: '70%' }}>
                          {g.goal_text}
                        </div>
                        <Badge bg={statusVariant(g.status || '')} pill>
                          {g.status ? g.status.replace('-', ' ') : '—'}
                        </Badge>
                      </div>

                      <div className="small text-muted mb-2">
                        <div>
                          <span className="text-secondary">Term:</span>{' '}
                          <span className="text-dark">{g.term || 'Not set'}</span>
                        </div>
                        <div>
                          <span className="text-secondary">Due:</span>{' '}
                          <span className="text-dark">
                            {g.due_date
                              ? new Date(g.due_date).toLocaleDateString()
                              : 'No due date'}
                          </span>
                        </div>
                      </div>

                      {isEditing && (
                        <div className="mb-2">
                          <Form.Control
                            as="textarea"
                            rows={2}
                            className="mb-2"
                            value={editingGoalText}
                            onChange={(e) => setEditingGoalText(e.target.value)}
                            placeholder="Goal"
                          />
                          <div className="d-flex flex-wrap gap-2">
                            <Form.Select
                              value={editingGoalTerm}
                              onChange={(e) => setEditingGoalTerm(e.target.value)}
                              className="flex-grow-1"
                            >
                              <option value="">Select Term</option>
                              <option value="Weekend">Weekend</option>
                              <option value="After-school">After-school</option>
                              <option value="Summer-dugsi">Summer-dugsi</option>
                            </Form.Select>
                            <Form.Select
                              value={editingGoalStatus}
                              onChange={(e) => setEditingGoalStatus(e.target.value)}
                              className="flex-grow-1"
                            >
                              <option value="in-progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="incomplete">Incomplete</option>
                            </Form.Select>
                            <Form.Control
                              type="date"
                              value={editingGoalDueDate || ''}
                              onChange={(e) => setEditingGoalDueDate(e.target.value)}
                              className="flex-grow-1"
                            />
                          </div>
                        </div>
                      )}

                      <div className="d-flex gap-2">
                        {isEditing ? (
                          <>
                            <Button size="sm" className="flex-fill" onClick={handleSave}>
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="flex-fill"
                              onClick={cancelEditGoal}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              className="flex-fill"
                              onClick={() => startEditGoal(g)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="flex-fill"
                              onClick={() =>
                                setGoalToDelete({
                                  id: g.id,
                                  text: g.goal_text,
                                })
                              }
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* confirm delete */}
      <Modal show={!!goalToDelete} onHide={() => setGoalToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete goal?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this goal?
          <div className="mt-2">
            <em>{goalToDelete?.text}</em>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setGoalToDelete(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default StudentGoalsModal;
